import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, ShieldPlus, X } from 'lucide-react';
import Button from '../../../components/commoncomponents/Button';
import {
  DEFAULT_ACCOUNTANT_SEAWEED_ACCESS_TABS,
  ROLE_OPTIONS,
  SEAWEED_ADMIN_TAB_OPTIONS,
  STAFF_USER_TYPE,
  getRoleOption,
} from '../constants/userRoles';

function UserFormModal({
  isOpen,
  mode = 'create',
  selectedBusinessName,
  branches,
  formValues,
  isRoleSelectionHidden = false,
  isSeaweedBusiness = false,
  onChange,
  onSeaweedTabChange,
  onToggleAllSeaweedTabs,
  onClose,
  onSubmit,
  submitError,
  submitting,
}) {
  const isEditing = mode === 'edit';
  const selectedRole = getRoleOption(formValues.role_name);
  const isStaffRole = selectedRole.userType === STAFF_USER_TYPE;
  const isAccountantRole = selectedRole.value === 'accountant' && isSeaweedBusiness;
  const seaweedAccessTabs = Array.isArray(formValues.seaweed_access_tabs)
    ? formValues.seaweed_access_tabs
    : DEFAULT_ACCOUNTANT_SEAWEED_ACCESS_TABS;
  const hasAllSeaweedTabs = SEAWEED_ADMIN_TAB_OPTIONS.every((item) =>
    seaweedAccessTabs.includes(item.value)
  );
  const SelectedRoleIcon = selectedRole.icon;
  const fieldTokens = isEditing
    ? {
        username: 'staff-edit-login',
        email: 'staff-edit-contact',
        password: 'staff-edit-secret',
      }
    : {
        username: 'staff-create-login',
        email: 'staff-create-contact',
        password: 'staff-create-secret',
      };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;

    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[rgba(17,17,17,0.26)] px-4 py-6 backdrop-blur-[4px]">
      <div className="flex max-h-[calc(100dvh-3rem)] w-full max-w-[560px] flex-col overflow-hidden rounded-[30px] border border-[#e9e0d8] bg-[linear-gradient(180deg,#fdfcfb_0%,#faf6f1_100%)] shadow-[0_24px_60px_rgba(17,17,17,0.14)]">
        <div className="shrink-0 border-b border-[#ebe3db] px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] border border-[#e7ddd3] bg-[#f7f4ef] text-[#6f5644]">
                <ShieldPlus className="h-6 w-6" />
              </div>
              <div>
                <h3 className="m-0 text-[1.45rem] font-semibold tracking-[-0.03em] text-[#111111] sm:text-[1.6rem]">
                  {isEditing ? 'Edit User' : 'Add User'}
                </h3>
                <p className="mt-2 text-sm leading-7 text-[#7f736b]">
                  {isEditing
                    ? `Update this account details${isRoleSelectionHidden ? '.' : ', role, and access.'}`
                    : 'Create an account under '}
                  {!isEditing ? (
                    <span className="font-semibold text-[#111111]">{selectedBusinessName}</span>
                  ) : null}
                  {!isEditing ? ' and set their role-based access clearly.' : null}
                </p>
              </div>
            </div>
            <button
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#e5dfd8] bg-white text-[#7f736b] shadow-[0_10px_24px_rgba(17,17,17,0.05)] transition hover:bg-[#faf7f3]"
              type="button"
              onClick={onClose}
              aria-label="Close user form"
            >
              <X className="h-4.5 w-4.5" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          <form className="grid gap-4" autoComplete="off" onSubmit={onSubmit}>
            <input
              aria-hidden="true"
              autoComplete="username"
              className="pointer-events-none absolute opacity-0"
              tabIndex={-1}
              type="text"
            />
            <input
              aria-hidden="true"
              autoComplete="new-password"
              className="pointer-events-none absolute opacity-0"
              tabIndex={-1}
              type="password"
            />

            <label className="grid gap-2.5 rounded-[22px] border border-[#e6ddd4] bg-white px-4 py-4 shadow-[0_8px_20px_rgba(17,17,17,0.04)]">
              <span className="text-sm font-semibold text-[#111111]">Username</span>
              <input
                data-field="username"
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                className="rounded-[16px] border border-[#e5dfd8] bg-[#fcfbf9] px-4 py-3 text-[#111111] outline-none placeholder:text-[#a1968d] focus:border-[#8e7663] focus:bg-white focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                type="text"
                name={fieldTokens.username}
                value={formValues.username}
                onChange={onChange}
                placeholder="Enter username"
              />
              <span className="text-sm leading-6 text-[#8b7a6d]">
                Choose a simple login name for this account.
              </span>
            </label>

            <label className="grid gap-2.5 rounded-[22px] border border-[#e6ddd4] bg-white px-4 py-4 shadow-[0_8px_20px_rgba(17,17,17,0.04)]">
              <span className="text-sm font-semibold text-[#111111]">Email</span>
              <input
                data-field="email"
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                className="rounded-[16px] border border-[#e5dfd8] bg-[#fcfbf9] px-4 py-3 text-[#111111] outline-none placeholder:text-[#a1968d] focus:border-[#8e7663] focus:bg-white focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                type="email"
                name={fieldTokens.email}
                value={formValues.email}
                onChange={onChange}
                placeholder="Enter email"
              />
              <span className="text-sm leading-6 text-[#8b7a6d]">
                Use an active email address if this user will receive account-related updates.
              </span>
            </label>

            {!isEditing ? (
              <label className="grid gap-2.5 rounded-[22px] border border-[#e6ddd4] bg-white px-4 py-4 shadow-[0_8px_20px_rgba(17,17,17,0.04)]">
                <span className="text-sm font-semibold text-[#111111]">Password</span>
                <input
                  data-field="password"
                  autoComplete="new-password"
                  className="rounded-[16px] border border-[#e5dfd8] bg-[#fcfbf9] px-4 py-3 text-[#111111] outline-none placeholder:text-[#a1968d] focus:border-[#8e7663] focus:bg-white focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                  type="password"
                  name={fieldTokens.password}
                  value={formValues.password}
                  onChange={onChange}
                  minLength={8}
                  placeholder="Enter temporary password"
                />
                <span className="text-sm leading-6 text-[#8b7a6d]">
                  Use at least 8 characters. The user will be asked to change it after first sign-in.
                </span>
              </label>
            ) : null}

            {!isRoleSelectionHidden ? (
              <label className="grid gap-2.5 rounded-[22px] border border-[#e6ddd4] bg-white px-4 py-4 shadow-[0_8px_20px_rgba(17,17,17,0.04)]">
                <span className="text-sm font-semibold text-[#111111]">Role</span>
                <select
                  className="rounded-[16px] border border-[#e5dfd8] bg-[#fcfbf9] px-4 py-3 text-[#111111] outline-none focus:border-[#8e7663] focus:bg-white focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                  name="role_name"
                  value={formValues.role_name}
                  onChange={onChange}
                >
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                <span className="text-sm leading-6 text-[#8b7a6d]">
                  Select the account role that best matches what this user should access.
                </span>
              </label>
            ) : null}

            <div className="rounded-[24px] border border-[#e6ddd4] bg-[#fcfbf9] px-4 py-4 shadow-[0_8px_20px_rgba(17,17,17,0.04)]">
              <div className="flex items-start gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-[18px] bg-white text-[#6f6258] shadow-[0_8px_18px_rgba(17,17,17,0.04)]">
                  <SelectedRoleIcon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-semibold text-[#111111]">{selectedRole.label} Access</p>
                  <p className="mt-1 text-sm leading-6 text-[#8b7a6d]">{selectedRole.description}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {isAccountantRole ? (
                  <>
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#e4dbd2] bg-white px-3 py-1.5 text-xs font-semibold text-[#5f5148]">
                      <input
                        className="h-3.5 w-3.5 accent-[#6f5644]"
                        type="checkbox"
                        checked={hasAllSeaweedTabs}
                        onChange={(event) => onToggleAllSeaweedTabs(event.target.checked)}
                      />
                      Check all Seaweeds tabs
                    </label>
                    <div className="grid w-full gap-2 pt-1 sm:grid-cols-2">
                      {SEAWEED_ADMIN_TAB_OPTIONS.map((item) => (
                        <label
                          key={item.value}
                          className="flex min-h-[40px] cursor-pointer items-center gap-2 rounded-[14px] border border-[#ece6df] bg-white px-3 py-2 text-sm text-[#6f6258]"
                        >
                          <input
                            className="h-4 w-4 accent-[#6f5644]"
                            type="checkbox"
                            checked={seaweedAccessTabs.includes(item.value)}
                            onChange={(event) =>
                              onSeaweedTabChange(item.value, event.target.checked)
                            }
                          />
                          <span>{item.label}</span>
                        </label>
                      ))}
                    </div>
                  </>
                ) : (
                  selectedRole.access.map((item) => (
                    <span
                      key={item}
                      className="inline-flex items-center gap-2 rounded-full border border-[#ece6df] bg-white px-3 py-1.5 text-xs text-[#6f6258]"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {item}
                    </span>
                  ))
                )}
              </div>
            </div>

            {isStaffRole ? (
              <label className="grid gap-2.5 rounded-[22px] border border-[#e6ddd4] bg-white px-4 py-4 shadow-[0_8px_20px_rgba(17,17,17,0.04)]">
                <span className="text-sm font-semibold text-[#111111]">Assign Branch</span>
                <select
                  className="rounded-[16px] border border-[#e5dfd8] bg-[#fcfbf9] px-4 py-3 text-[#111111] outline-none focus:border-[#8e7663] focus:bg-white focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                  name="branch_id"
                  value={formValues.branch_id}
                  onChange={onChange}
                >
                  <option value="">No specific branch</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                <span className="text-sm leading-6 text-[#8b7a6d]">
                  Leave this empty only if the user should not be limited to one specific branch yet.
                </span>
              </label>
            ) : (
              <div className="grid gap-2.5 rounded-[22px] border border-[#e6ddd4] bg-white px-4 py-4 shadow-[0_8px_20px_rgba(17,17,17,0.04)]">
                <span className="text-sm font-semibold text-[#111111]">Branch Access</span>
                <span className="text-sm leading-6 text-[#8b7a6d]">
                  Admin accounts are saved as user type 1 and can open the selected business portal
                  without a branch assignment.
                </span>
              </div>
            )}

            {submitError ? (
              <div className="rounded-[20px] border border-[#f0d4cb] bg-[#fff8f6] px-4 py-3 text-sm text-[#b4533f]">
                {submitError}
              </div>
            ) : null}

            <div className="mt-1 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                className="w-full justify-center rounded-[18px] px-5 py-3 text-[15px] sm:w-auto"
                variant="secondary"
                type="button"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                className="w-full justify-center rounded-[18px] px-5 py-3 text-[15px] sm:w-auto"
                type="submit"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Save User'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default UserFormModal;
